import { useMemo } from 'react';

import { isStudentCollectionType } from '@/helpers/collectionTypes';
import { hasDepartmentAccess } from '@/helpers/departmentPermissions';
import { useFlattenDepartmentTree } from '@/hooks/useFlattenDepartmentTree';
import { useAuth } from '@/stores/auth';

/**
 * Returns whether the current user has access to at least one non-school
 * (non-student collection type) department. Organization-level users
 * implicitly have access to all departments, so they get `true` as
 * long as a non-school department exists in the tree. Returns `undefined`
 * while the department tree is still loading.
 */
export const useHasNonSchoolDepartmentAccess = (): boolean | undefined => {
  const { userProfile } = useAuth();
  const isSysadmin = !userProfile?.organization_id;

  const { flattenedDepartments } = useFlattenDepartmentTree(!isSysadmin);
  const permissions = useAuth((s) => s.permissions) ?? [];
  const isOrgUser = permissions.length > 0;

  return useMemo(() => {
    if (isSysadmin) return false;

    if (!flattenedDepartments) return undefined;

    const nonSchoolDepartments = flattenedDepartments.filter(
      (dept) => !isStudentCollectionType(dept.type),
    );

    if (nonSchoolDepartments.length === 0) return false;

    if (isOrgUser) return true;

    return nonSchoolDepartments.some((dept) => hasDepartmentAccess(dept.id));
  }, [flattenedDepartments, isOrgUser, isSysadmin]);
};
